import { useState, useEffect, useRef } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Alert,

  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Paper,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  ListItemText,
  OutlinedInput,
  SelectChangeEvent,
  Stack,
  Skeleton,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as PreviewIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material'
import {
  outreachTemplatesApi,
  OutreachTemplate,
  OutreachTemplateCategory,
} from '../services/api'
import { useNotification } from '../hooks/useNotification'
import PageHeader from '../components/common/PageHeader'
import ResponsiveSheet from '../components/common/ResponsiveSheet'
import PageHelpButton from '../components/PageHelpButton'
import { OUTREACH_STAGE_OPTIONS } from '../constants/networking'

const MERGE_FIELDS = [
  { key: 'contactName', label: 'Contact Name' },
  { key: 'company', label: 'Company' },
  { key: 'sharedConnection', label: 'Shared Connection' },
  { key: 'roleTitle', label: 'Role Title' },
]

const SENIORITY_LEVELS = [
  { value: '', label: 'All Levels' },
  { value: 'junior', label: 'Junior' },
  { value: 'mid', label: 'Mid-Level' },
  { value: 'senior', label: 'Senior' },
  { value: 'director', label: 'Director' },
  { value: 'executive', label: 'Executive' },
]

// Templates are assigned to outreach stages, the same vocabulary as checklists.
const CONTACT_STATUSES = OUTREACH_STAGE_OPTIONS.map(({ value, label }) => ({ value, label }))

const SAMPLE_DATA: Record<string, string> = {
  contactName: 'Sarah Chen',
  company: 'Acme Corp',
  sharedConnection: 'Alex Rivera',
  roleTitle: 'Senior Product Manager',
}

function resolveTemplate(body: string): string {
  return body.replace(/\{(\w+)\}/g, (_, key) => SAMPLE_DATA[key] || `{${key}}`)
}

// Sortable table row component
function SortableTemplateRow({
  template,
  onEdit,
  onDelete,
  onToggleActive,
}: {
  template: OutreachTemplate
  onEdit: (t: OutreachTemplate) => void
  onDelete: (t: OutreachTemplate) => void
  onToggleActive: (t: OutreachTemplate) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: template.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : template.isActive === 'true' ? 1 : 0.5,
  }

  return (
    <TableRow ref={setNodeRef} style={style} {...attributes}>
      <TableCell sx={{ width: 40, cursor: 'grab' }} {...listeners}>
        <DragIcon fontSize="small" color="action" />
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={500}>{template.title}</Typography>
        {template.talkingPoints && template.talkingPoints.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {template.talkingPoints.length} talking point{template.talkingPoints.length !== 1 ? 's' : ''}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Chip label={`v${template.version}`} size="small" variant="outlined" />
      </TableCell>
      <TableCell>
        {template.seniorityLevel ? (
          <Chip label={template.seniorityLevel} size="small" />
        ) : (
          <Typography variant="caption" color="text.secondary">All</Typography>
        )}
      </TableCell>
      <TableCell>
        {template.assignedStages && template.assignedStages.length > 0 ? (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {template.assignedStages.slice(0, 2).map((s) => (
              <Chip key={s} label={CONTACT_STATUSES.find((cs) => cs.value === s)?.label || s} size="small" variant="outlined" />
            ))}
            {template.assignedStages.length > 2 && (
              <Chip label={`+${template.assignedStages.length - 2}`} size="small" variant="outlined" />
            )}
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary">All statuses</Typography>
        )}
      </TableCell>
      <TableCell>
        {template.clientId === 'default' ? (
          <Chip label="Default" size="small" color="primary" variant="outlined" />
        ) : (
          <Chip label="Client Override" size="small" color="warning" variant="outlined" />
        )}
      </TableCell>
      <TableCell>
        <Switch
          checked={template.isActive === 'true'}
          onChange={() => onToggleActive(template)}
          size="small"
        />
      </TableCell>
      <TableCell align="right">
        <IconButton size="small" onClick={() => onEdit(template)}>
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => onDelete(template)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  )
}

export default function ManageOutreachTemplates() {
  const [categories, setCategories] = useState<OutreachTemplateCategory[]>([])
  const [templates, setTemplates] = useState<OutreachTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState(0)
  const { showSuccess, showError } = useNotification()

  // Template dialog state
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<OutreachTemplate | null>(null)
  const [formCategoryId, setFormCategoryId] = useState('')
  const [formTitle, setFormTitle] = useState('')
  const [formBody, setFormBody] = useState('')
  const [formTalkingPoints, setFormTalkingPoints] = useState('')
  const [formSeniorityLevel, setFormSeniorityLevel] = useState('')
  const [formAssignedStatuses, setFormAssignedStatuses] = useState<string[]>([])
  const [formIsDefault, setFormIsDefault] = useState(true)
  const [formClientId, setFormClientId] = useState('')
  const [saving, setSaving] = useState(false)
  // A refused save, said inside the dialog. The toast alone sat behind the very
  // surface holding the copy that failed to save.
  const [templateSaveError, setTemplateSaveError] = useState('')
  const [showPreview, setShowPreview] = useState(false)
  const bodyRef = useRef<HTMLTextAreaElement>(null)

  // Category dialog state
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<OutreachTemplateCategory | null>(null)
  const [categoryName, setCategoryName] = useState('')
  const [savingCategory, setSavingCategory] = useState(false)

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deletingItem, setDeletingItem] = useState<{ type: 'template' | 'category'; id: string; name: string } | null>(null)

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const fetchData = async () => {
    setLoading(true)
    try {
      const result = await outreachTemplatesApi.manage({ action: 'list' }) as { categories: OutreachTemplateCategory[]; templates: OutreachTemplate[] }
      setCategories(result.categories || [])
      setTemplates(result.templates || [])
    } catch (err) {
      console.error('Failed to load templates:', err)
      setError('Failed to load templates')
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchData()
  }, [])

  // ──────────────────────────────────────
  // Drag-to-reorder
  // ──────────────────────────────────────

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = filteredTemplates.findIndex((t) => t.id === active.id)
    const newIndex = filteredTemplates.findIndex((t) => t.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    const reordered = arrayMove(filteredTemplates, oldIndex, newIndex)

    // Optimistic update
    const newTemplates = templates.map((t) => {
      const idx = reordered.findIndex((r) => r.id === t.id)
      if (idx !== -1) return { ...t, sortOrder: idx }
      return t
    })
    setTemplates(newTemplates)

    try {
      await outreachTemplatesApi.manage({ action: 'reorder', ids: reordered.map((t) => t.id) })
    } catch (err) {
      console.error('Failed to reorder templates:', err)
      showError('Failed to reorder templates')
      fetchData()
    }
  }

  // ──────────────────────────────────────
  // Category management
  // ──────────────────────────────────────

  const openCreateCategory = () => {
    setEditingCategory(null)
    setCategoryName('')
    setCategoryDialogOpen(true)
  }

  const openEditCategory = (cat: OutreachTemplateCategory) => {
    setEditingCategory(cat)
    setCategoryName(cat.name)
    setCategoryDialogOpen(true)
  }

  const handleSaveCategory = async () => {
    if (!categoryName.trim()) return
    setSavingCategory(true)
    try {
      if (editingCategory) {
        await outreachTemplatesApi.manage({ action: 'update-category', id: editingCategory.id, name: categoryName.trim() })
        showSuccess('Category updated')
      } else {
        await outreachTemplatesApi.manage({ action: 'create-category', name: categoryName.trim() })
        showSuccess('Category created')
      }
      setCategoryDialogOpen(false)
      fetchData()
    } catch (err) {
      console.error('Failed to save category:', err)
      showError('Failed to save category')
    }
    setSavingCategory(false)
  }

  const confirmDeleteCategory = (cat: OutreachTemplateCategory) => {
    setDeletingItem({ type: 'category', id: cat.id, name: cat.name })
    setDeleteConfirmOpen(true)
  }

  // ──────────────────────────────────────
  // Template management
  // ──────────────────────────────────────

  const openCreateTemplate = () => {
    setEditingTemplate(null)
    const currentCat = categories[activeTab]
    setFormCategoryId(currentCat?.id || '')
    setFormTitle('')
    setFormBody('')
    setFormTalkingPoints('')
    setFormSeniorityLevel('')
    setFormAssignedStatuses([])
    setFormIsDefault(true)
    setFormClientId('')
    setShowPreview(false)
    setTemplateDialogOpen(true)
  }

  const openEditTemplate = (template: OutreachTemplate) => {
    setEditingTemplate(template)
    setFormCategoryId(template.categoryId)
    setFormTitle(template.title)
    setFormBody(template.body)
    setFormTalkingPoints((template.talkingPoints || []).join('\n'))
    setFormSeniorityLevel(template.seniorityLevel || '')
    setFormAssignedStatuses(template.assignedStages || [])
    setFormIsDefault(template.clientId === 'default')
    setFormClientId(template.clientId === 'default' ? '' : template.clientId)
    setShowPreview(false)
    setTemplateDialogOpen(true)
  }

  const insertMergeField = (key: string) => {
    const textarea = bodyRef.current
    if (!textarea) {
      setFormBody((prev) => prev + `{${key}}`)
      return
    }
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const text = formBody
    const newText = text.substring(0, start) + `{${key}}` + text.substring(end)
    setFormBody(newText)
    setTimeout(() => {
      textarea.focus()
      const newPos = start + key.length + 2
      textarea.setSelectionRange(newPos, newPos)
    }, 0)
  }

  const handleSaveTemplate = async () => {
    if (!formTitle.trim() || !formBody.trim() || !formCategoryId) return
    setTemplateSaveError('')
    setSaving(true)
    try {
      const talkingPoints = formTalkingPoints.split('\n').map((t) => t.trim()).filter(Boolean)
      const payload = {
        categoryId: formCategoryId,
        title: formTitle.trim(),
        body: formBody,
        talkingPoints,
        seniorityLevel: formSeniorityLevel || null,
        assignedStages: formAssignedStatuses,
        isDefault: formIsDefault,
        clientId: formIsDefault ? 'default' : (formClientId || 'default'),
      }
      if (editingTemplate) {
        await outreachTemplatesApi.manage({ action: 'update', id: editingTemplate.id, ...payload })
        showSuccess('Template updated')
      } else {
        await outreachTemplatesApi.manage({ action: 'create', ...payload })
        showSuccess('Template created')
      }
      setTemplateDialogOpen(false)
      fetchData()
    } catch (err) {
      console.error('Failed to save template:', err)
      setTemplateSaveError(
        err instanceof Error && err.message
          ? err.message
          : 'The template could not be saved. Nothing has been lost — try again, and tell us if it keeps happening.'
      )
    }
    setSaving(false)
  }

  const confirmDeleteTemplate = (template: OutreachTemplate) => {
    setDeletingItem({ type: 'template', id: template.id, name: template.title })
    setDeleteConfirmOpen(true)
  }

  const handleToggleActive = async (template: OutreachTemplate) => {
    try {
      const newActive = template.isActive === 'true' ? 'false' : 'true'
      await outreachTemplatesApi.manage({ action: 'update', id: template.id, isActive: newActive })
      showSuccess(newActive === 'true' ? 'Template activated' : 'Template deactivated')
      fetchData()
    } catch (err) {
      console.error('Failed to toggle template:', err)
      showError('Failed to update template')
    }
  }

  const handleDelete = async () => {
    if (!deletingItem) return
    try {
      if (deletingItem.type === 'template') {
        await outreachTemplatesApi.manage({ action: 'delete', id: deletingItem.id })
        showSuccess('Template deleted')
      } else {
        await outreachTemplatesApi.manage({ action: 'delete-category', id: deletingItem.id })
        showSuccess('Category deleted')
        setActiveTab(0)
      }
      setDeleteConfirmOpen(false)
      setDeletingItem(null)
      fetchData()
    } catch (err) {
      console.error('Failed to delete:', err)
      showError('Failed to delete')
    }
  }

  const handleStatusSelectChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value
    setFormAssignedStatuses(typeof value === 'string' ? value.split(',') : value)
  }

  // Filter templates for the active tab
  const currentCategory = categories[activeTab]
  const filteredTemplates = currentCategory
    ? templates.filter((t) => t.categoryId === currentCategory.id).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    : []

  // The page's own shape: header, category tabs, then the template list. A
  // centred spinner threw all of that away and rebuilt it on every load.
  if (loading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Skeleton variant="text" width={220} height={26} />
          <Stack direction="row" spacing={1}>
            <Skeleton variant="rounded" width={150} height={44} />
            <Skeleton variant="rounded" width={140} height={44} />
          </Stack>
        </Box>
        <Skeleton variant="rounded" height={44} sx={{ mb: 2 }} />
        <Stack spacing={1.5}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={84} />
          ))}
        </Stack>
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <PageHeader
          title="Outreach Templates"
          help={<PageHelpButton articleId="page-outreach-templates" />}
          actions={
            <>
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={openCreateCategory}
                sx={{ minHeight: 44 }}
              >
                Manage Categories
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openCreateTemplate}
                disabled={categories.length === 0}
                sx={{ minHeight: 44 }}
              >
                New Template
              </Button>
            </>
          }
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {categories.length === 0 ? (
        <Alert severity="info">
          No categories yet. Click "Manage Categories" to create your first outreach template category.
        </Alert>
      ) : (
        <>
          {/* Category tabs */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ flex: 1 }}
            >
              {categories.map((cat) => (
                <Tab key={cat.id} label={cat.name} />
              ))}
            </Tabs>
            {currentCategory && (
              <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                <Tooltip title="Edit category">
                  <IconButton size="small" onClick={() => openEditCategory(currentCategory)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete category">
                  <IconButton size="small" onClick={() => confirmDeleteCategory(currentCategory)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>

          {/* Template table with drag-to-reorder */}
          <Card>
            <CardContent sx={{ p: 0 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 40 }} />
                      <TableCell>Title</TableCell>
                      <TableCell>Version</TableCell>
                      <TableCell>Seniority</TableCell>
                      <TableCell>Contact Statuses</TableCell>
                      <TableCell>Scope</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={filteredTemplates.map((t) => t.id)} strategy={verticalListSortingStrategy}>
                      <TableBody>
                        {filteredTemplates.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                No templates in this category. Click "New Template" to create one.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredTemplates.map((t) => (
                            <SortableTemplateRow
                              key={t.id}
                              template={t}
                              onEdit={openEditTemplate}
                              onDelete={confirmDeleteTemplate}
                              onToggleActive={handleToggleActive}
                            />
                          ))
                        )}
                      </TableBody>
                    </SortableContext>
                  </DndContext>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}

      {/* Template Create/Edit Dialog */}
      <ResponsiveSheet
        open={templateDialogOpen}
        // Backdrop clicks do not discard a draft; Cancel and Escape still do.
        onClose={(_e, reason) => { if (reason !== 'backdropClick') setTemplateDialogOpen(false) }}
        maxWidth="md"
        fullScreenBelow={768}
      >
        <DialogTitle sx={{ fontSize: '1rem', lineHeight: '1.375rem', fontWeight: 600 }}>
          {editingTemplate ? 'Edit Template' : 'New Template'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {templateSaveError && (
              <Alert severity="error" onClose={() => setTemplateSaveError('')}>
                {templateSaveError}
              </Alert>
            )}
            {/* Category */}
            <FormControl size="small" fullWidth>
              <InputLabel>Category</InputLabel>
              <Select value={formCategoryId} label="Category" onChange={(e) => setFormCategoryId(e.target.value)}>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Title */}
            <TextField
              label="Template Title"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              size="small"
              fullWidth
              placeholder="e.g., Warm Introduction via Mutual Connection"
            />

            {/* Merge field buttons */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Insert merge fields:
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {MERGE_FIELDS.map((f) => (
                  <Chip
                    key={f.key}
                    label={`{${f.key}}`}
                    size="small"
                    onClick={() => insertMergeField(f.key)}
                    clickable
                    variant="outlined"
                    color="primary"
                  />
                ))}
              </Box>
            </Box>

            {/* Body */}
            <TextField
              label="Template Body"
              value={formBody}
              onChange={(e) => setFormBody(e.target.value)}
              size="small"
              fullWidth
              multiline
              rows={6}
              inputRef={bodyRef}
              placeholder="Hi {contactName}, I hope you're doing well! I noticed we share a connection through {sharedConnection}..."
            />

            {/* Preview toggle */}
            {formBody && (
              <Box>
                <Button
                  size="small"
                  startIcon={<PreviewIcon />}
                  onClick={() => setShowPreview(!showPreview)}
                  variant="text"
                >
                  {showPreview ? 'Hide Preview' : 'Show Preview'}
                </Button>
                {showPreview && (
                  <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'background.default' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Preview with sample data:
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxWidth: '68ch' }}>
                      {resolveTemplate(formBody)}
                    </Typography>
                  </Paper>
                )}
              </Box>
            )}

            {/* Talking Points */}
            <TextField
              label="Talking Points (one per line)"
              value={formTalkingPoints}
              onChange={(e) => setFormTalkingPoints(e.target.value)}
              size="small"
              fullWidth
              multiline
              rows={3}
              placeholder={"Ask who the hiring manager is\nMention the role you're targeting\nRequest a referral if conversation goes well"}
              helperText="Displayed as a checklist alongside the template when the client uses it"
            />

            {/* Seniority Level */}
            <FormControl size="small" fullWidth>
              <InputLabel>Seniority Level</InputLabel>
              <Select
                value={formSeniorityLevel}
                label="Seniority Level"
                onChange={(e) => setFormSeniorityLevel(e.target.value)}
              >
                {SENIORITY_LEVELS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Contact Status Assignment */}
            <FormControl size="small" fullWidth>
              <InputLabel>Show for Contact Statuses</InputLabel>
              <Select
                multiple
                value={formAssignedStatuses}
                onChange={handleStatusSelectChange}
                input={<OutlinedInput label="Show for Contact Statuses" />}
                renderValue={(selected) =>
                  selected.map((s) => CONTACT_STATUSES.find((cs) => cs.value === s)?.label || s).join(', ')
                }
              >
                {CONTACT_STATUSES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    <Checkbox checked={formAssignedStatuses.includes(s.value)} size="small" />
                    <ListItemText primary={s.label} />
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Leave empty to show for all contact statuses
              </Typography>
            </FormControl>

            {/* Default / Client override */}
            <FormControlLabel
              control={<Switch checked={formIsDefault} onChange={(e) => setFormIsDefault(e.target.checked)} />}
              label="Default template (applies to all clients)"
            />
            {!formIsDefault && (
              <TextField
                label="Client ID (for client-specific override)"
                value={formClientId}
                onChange={(e) => setFormClientId(e.target.value)}
                size="small"
                fullWidth
                placeholder="Enter client ID"
              />
            )}

            {/* Version history for editing */}
            {editingTemplate && editingTemplate.previousVersions && editingTemplate.previousVersions.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2">
                    Version History ({editingTemplate.previousVersions.length} previous version{editingTemplate.previousVersions.length !== 1 ? 's' : ''})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {editingTemplate.previousVersions
                    .slice()
                    .reverse()
                    .map((pv, idx) => (
                      <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" fontWeight={500}>
                            Version {pv.version}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(pv.updatedAt).toLocaleDateString()} by {pv.updatedBy}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
                          {pv.body}
                        </Typography>
                      </Paper>
                    ))}
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 2 }}>
          <Button onClick={() => setTemplateDialogOpen(false)} sx={{ minHeight: 44 }}>Cancel</Button>
          <Button
            onClick={handleSaveTemplate}
            variant="contained"
            sx={{ minHeight: 44 }}
            disabled={saving || !formTitle.trim() || !formBody.trim() || !formCategoryId}
          >
            {saving ? 'Saving...' : editingTemplate ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </ResponsiveSheet>

      {/* Category Create/Edit Dialog */}
      <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingCategory ? 'Edit Category' : 'New Category'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              label="Category Name"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              size="small"
              fullWidth
              placeholder="e.g., Warm Introduction"
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveCategory}
            variant="contained"
            disabled={savingCategory || !categoryName.trim()}
          >
            {savingCategory ? 'Saving...' : editingCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete {deletingItem?.type === 'category' ? 'Category' : 'Template'}</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete "{deletingItem?.name}". It cannot be undone.
            {deletingItem?.type === 'category' && ' All templates in this category will be deactivated.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
